import { useEffect, useState } from "react";
import axios from "axios";
import { X, Send, Package } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { COUNTRIES, DEFAULT_DIAL } from "../lib/countries";
import { partnerStrings } from "../lib/partnersData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputBase =
  "w-full rounded-lg border border-slate-300 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2";

const label =
  "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500";

export const QuoteModal = ({
  open,
  onClose,
  product,
  brand = "Intelbras",
  imageFolder = "/intelbras",
  theme,
}) => {
  const { lang } = useLang();
  const s = (partnerStrings[lang] || partnerStrings.pt).quote_modal;
  const cta = partnerStrings[lang]?.card_quote_cta || partnerStrings.pt.card_quote_cta;

  const t = theme || {
    modalHeaderBg: "from-emerald-50 via-[#d8e8f5] to-blue-50",
    modalBrandBadge: "border-emerald-200 text-emerald-700",
    modalInputFocus: "focus:border-emerald-500 focus:ring-emerald-500/20",
    modalSubmit:
      "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/25 hover:shadow-emerald-600/40",
  };

  const [form, setForm] = useState({
    name: "",
    email: "",
    dial: DEFAULT_DIAL,
    phone: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !product) return null;

  const productLabel = product[lang] || product.pt;
  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(s.required);
      return;
    }
    setSubmitting(true);
    try {
      const subject = `${s.subject_prefix} — ${brand} · ${productLabel}`;
      const body = [
        `${s.product_label}: ${productLabel} (${brand})`,
        form.company && `${s.company}: ${form.company}`,
        "",
        form.message,
      ]
        .filter(Boolean)
        .join("\n");

      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone ? `+${form.dial} ${form.phone}`.trim() : "",
        subject,
        message: body,
        locale: lang,
      });
      toast.success(s.success);
      setForm({
        name: "",
        email: "",
        dial: DEFAULT_DIAL,
        phone: "",
        company: "",
        message: "",
      });
      onClose?.();
    } catch (err) {
      toast.error(s.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="quote-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-modal-title"
    >
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        {/* Header with product preview */}
        <div className={`relative flex items-start gap-4 border-b border-slate-200 bg-gradient-to-br p-6 md:p-8 ${t.modalHeaderBg}`}>
          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white md:h-24 md:w-24">
            <img
              src={`${imageFolder}/${product.slug}.png`}
              alt={productLabel}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className={`inline-flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] ${t.modalBrandBadge}`}>
              <Package className="h-3 w-3" strokeWidth={2.5} /> {brand}
            </div>
            <h3
              id="quote-modal-title"
              className="font-heading mt-2 text-xl font-bold leading-snug text-slate-900 md:text-2xl"
            >
              {cta}
              <span className="block text-sm font-medium text-slate-600 md:text-base">
                {productLabel}
              </span>
            </h3>
            <p className="mt-1 text-xs text-slate-500 md:text-sm">{s.subtitle}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            data-testid="quote-modal-close"
            aria-label="Fechar"
            className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900"
          >
            <X className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmit}
          data-testid="quote-modal-form"
          className="max-h-[65vh] overflow-y-auto p-6 md:p-8"
          noValidate
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={label}>{s.name} *</label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                className={`${inputBase} ${t.modalInputFocus}`}
                placeholder={s.name_ph}
                data-testid="quote-input-name"
              />
            </div>
            <div>
              <label className={label}>{s.email} *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                className={`${inputBase} ${t.modalInputFocus}`}
                placeholder={s.email_ph}
                data-testid="quote-input-email"
              />
            </div>
            <div className="md:col-span-2">
              <label className={label}>{s.phone}</label>
              <div className="flex gap-2">
                <select
                  name="dial"
                  value={form.dial}
                  onChange={onChange}
                  className={`appearance-none rounded-lg border border-slate-300 bg-slate-50 py-2.5 pl-3 pr-6 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 ${t.modalInputFocus}`}
                  data-testid="quote-input-dial"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.iso} value={c.dial}>
                      {c.flag} +{c.dial}
                    </option>
                  ))}
                </select>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  className={`${inputBase} ${t.modalInputFocus} flex-1`}
                  placeholder={s.phone_ph}
                  data-testid="quote-input-phone"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className={label}>{s.company}</label>
              <input
                name="company"
                value={form.company}
                onChange={onChange}
                className={`${inputBase} ${t.modalInputFocus}`}
                placeholder={s.company_ph}
                data-testid="quote-input-company"
              />
            </div>
            <div className="md:col-span-2">
              <label className={label}>{s.message} *</label>
              <textarea
                name="message"
                rows={4}
                value={form.message}
                onChange={onChange}
                className={`${inputBase} ${t.modalInputFocus} resize-none`}
                placeholder={s.message_ph}
                data-testid="quote-input-message"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="quote-submit-button"
            className={`group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all active:scale-95 disabled:opacity-60 md:w-auto ${t.modalSubmit}`}
          >
            {submitting ? s.sending : s.submit}
            <Send
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.5}
            />
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuoteModal;
